// The receiving end of a handler='open' action button on a TRANSACTION.
//
// Another screen's button ("Full receipt" on a purchase order) opened THIS
// object with values from the row it was clicked on. This hook does what the
// button declared, in order, exactly as a person at this screen would:
//
//   1. open the record — a new one ('add'), the one matching `match`
//      ('edit'), or that one when it exists and a new one otherwise
//      ('edit_or_add'). Several matches open the LIST, filtered to them, so
//      the person picks; nothing is set on a record they did not choose.
//      'view' opens the matching record read-only and stops there.
//   2. set each `set` column, one at a time, and — unless the step says not
//      to — fire that column's item_change and WAIT for it, so the next
//      column (and every later step) sees the values the first one filled.
//   3. run the `then` steps: a Line Entry command on a line form (the lines
//      of the purchase order arriving on the receipt), or an action button of
//      this form, applied like a click on it.
//
// Nothing is saved. The person reviews the record and saves it, so every rule
// of this object — rights, validations, approvals, the line links' checks —
// applies as it would to values they typed. A step that fails stops the
// sequence and says which one, leaving the record open and editable.

import { useCallback, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'

import { columnFormKey } from '../utils'
import type { OpenScreenPayload, OpenActionThenStep, SqlModel } from '../types'
import type { RunActionResponse } from '../contracts'
import type { TxnNotifier } from '../context'
import type { HeaderEdit, LineCommandRequest } from '../lineEntry/LineEntryBar'
import type { LineRunOutcome } from '../lineEntry/useLineEntry'
import type { HeaderItemChangeOutcome } from './useHeaderItemChange'
import type { ListFilter, TxnScreenRuntime } from './contract'
import { attemptWithWarningConfirm } from '../warnings'
import { attemptActionWithConfirm, isActionConfirmCancelled } from '../ActionConfirmModal'

/** The list narrowed to the records an 'open' button matched. */
export interface OpenListFilter {
  filters: ListFilter[]
  /** "2 Goods Receipts match PO_NO 4501" — shown with a way to clear it. */
  label: string
}

/** A line form's command, as the sequence addresses it. */
export interface LineFormCommands {
  formNo: string
  commands: string[]
}

export interface UseOpenSequenceOpts {
  objName: string
  /** The header form's model and form_no. */
  sm: SqlModel | null | undefined
  headerNo: string
  objTitle: string
  runtime: TxnScreenRuntime
  notify: TxnNotifier
  /** Line forms with a Line Entry, and the commands each one offers. */
  lineForms: LineFormCommands[]
  /** Columns a line form's `on_header_change` listens to (form keys). */
  headerChangeColumns: string[]
  startAdd: () => Promise<Record<string, unknown> | undefined>
  startEdit: (txnId: string, opts?: { forceView?: boolean }) => Promise<{ header: Record<string, unknown>; viewOnly: boolean } | undefined>
  setFormValues: Dispatch<SetStateAction<Record<string, unknown>>>
  setFormDirty: (dirty: boolean) => void
  setDynamicProtect: Dispatch<SetStateAction<Record<string, 0 | 1>>>
  setFormBanner: (banner: string | null) => void
  setHeaderEdit: Dispatch<SetStateAction<HeaderEdit | null>>
  itemChangeNow: (fieldName: string, header: Record<string, unknown>) => Promise<HeaderItemChangeOutcome>
  applyActionResult: (res: RunActionResponse) => void
  /** Narrow the list to several matches (null clears it). */
  showMatches: (filter: OpenListFilter | null) => void
  /** The open record's id, read when an action step runs. */
  currentTxnId: () => string | null
  /** The caller's rights on THIS object, read when the record is chosen. The
   *  server already refused a button whose target the caller can use in no
   *  way; these settle the edit-or-add case it cannot (may add, may not edit
   *  the record that turned out to exist — or the other way round). */
  canAdd: () => boolean
  canEdit: () => boolean
  confirmWarnings: Parameters<typeof attemptWithWarningConfirm>[1]
  confirmAction: Parameters<typeof attemptActionWithConfirm>[1]
}

function norm(v: unknown): string {
  return String(v ?? '').trim().replace(/\s+/g, '_').toLowerCase()
}

interface ColumnSpelling {
  formKey: string
  dbName: string
  label: string
}

/** Every spelling of every column of the form → its form key / db column. */
function columnIndex(sm: SqlModel | null | undefined): Map<string, ColumnSpelling> {
  const out = new Map<string, ColumnSpelling>()
  for (const w of sm?.columns || []) {
    const col = w?.column
    if (!col) continue
    const formKey = columnFormKey(col)
    const dbName = norm((col as { db_name?: string }).db_name) || formKey
    const entry = { formKey, dbName, label: String(col.heading || col.name || formKey) }
    for (const spelling of [col.name, (col as { db_name?: string }).db_name, formKey]) {
      const key = norm(spelling)
      if (key && !out.has(key)) out.set(key, entry)
    }
  }
  return out
}

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { error?: { message?: string }; detail?: unknown } }; message?: string }
  return e?.response?.data?.error?.message
    || (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail as string : '')
    || e?.message || String(err)
}

/** What a person is told when the button's target refuses what it was for. */
export const OPEN_TARGET_DENIED = 'You do not have rights to perform this action.'

export function useOpenSequence(o: UseOpenSequenceOpts) {
  // Latest options in a ref: the sequence awaits between steps, and must read
  // the screen as it is now, not as it was when the button was clicked.
  const ref = useRef(o)
  ref.current = o
  const [running, setRunning] = useState<string | null>(null)
  const [lineRequest, setLineRequest] = useState<(LineCommandRequest & { formNo: string }) | null>(null)
  const lineWaiters = useRef(new Map<number, (outcome: LineRunOutcome) => void>())
  const lineSeq = useRef(0)

  /** A line command finished (reported by the headless LineEntryBar). */
  const onLineRequestDone = useCallback((n: number, outcome: LineRunOutcome) => {
    const resolve = lineWaiters.current.get(n)
    lineWaiters.current.delete(n)
    resolve?.(outcome)
  }, [])

  const runLineCommand = useCallback(
    (step: OpenActionThenStep): Promise<LineRunOutcome | string> => {
      const cur = ref.current
      const command = String(step.command || '').trim()
      const wanted = String(step.form_no || '').trim()
      const form = wanted
        ? cur.lineForms.find(f => f.formNo === wanted)
        : cur.lineForms.find(f => f.commands.includes(command))
      if (!form) {
        return Promise.resolve(wanted
          ? `form ${wanted} has no line entry`
          : `no line form offers the command "${command}"`)
      }
      if (!form.commands.includes(command)) {
        return Promise.resolve(`form ${form.formNo} has no line command "${command}"`)
      }
      const n = ++lineSeq.current
      return new Promise((resolve) => {
        lineWaiters.current.set(n, resolve)
        setLineRequest({ formNo: form.formNo, command, select: step.select === 'ask' ? 'ask' : 'all', n })
      })
    }, [])

  const runActionStep = useCallback(
    async (step: OpenActionThenStep, values: Record<string, unknown>): Promise<string | null> => {
      const cur = ref.current
      const name = String(step.action_name || '').trim()
      const formNo = String(step.form_no || '').trim() || cur.headerNo
      const txnId = cur.currentTxnId()
      try {
        const res = await attemptActionWithConfirm(
          (confirm) => attemptWithWarningConfirm(
            (ignore) => cur.runtime.execute(cur.objName, 'run_action', {
              form_no: formNo,
              action_name: name,
              header: values,
              header_values: {},
              ...(txnId ? { txn_id: txnId } : {}),
              ...(ignore.length ? { ignore_warnings: ignore } : {}),
              ...(confirm ? { confirm } : {}),
            }),
            cur.confirmWarnings,
          ) as Promise<RunActionResponse>,
          cur.confirmAction,
        )
        const notifyInfo = cur.notify.info ?? cur.notify.success
        res.prompts?.forEach(p => notifyInfo(p.message))
        if (res.handler === 'item_change' || res.handler === 'select_grid') {
          cur.applyActionResult(res)
          if (res.message) cur.notify.success(res.message)
          return null
        }
        if (res.handler === 'message') {
          if (res.message) cur.notify.success(res.message)
          return null
        }
        return `the action "${name}" ${res.handler === 'system' ? 'is a built-in operation' : `opens a ${res.handler} window`} and cannot run as a follow-up step`
      } catch (err) {
        if (isActionConfirmCancelled(err)) return 'cancelled'
        return errorText(err)
      }
    }, [])

  /** Run one 'open' payload against this screen. */
  const runOpen = useCallback(async (payload: OpenScreenPayload): Promise<void> => {
    const cur = ref.current
    const title = payload.title || payload.actionName || cur.objTitle
    const index = columnIndex(cur.sm)
    setRunning(title)
    cur.showMatches(null)
    try {
      // ── 1. Which record ───────────────────────────────────────────────
      let values: Record<string, unknown> | undefined
      if (payload.mode === 'add') {
        if (!cur.canAdd()) { cur.notify.error(OPEN_TARGET_DENIED); return }
        values = await cur.startAdd()
      } else {
        if (payload.matchMissing.length) {
          cur.notify.error(`Could not look up the ${cur.objTitle}: ${payload.matchMissing.join(', ')} `
            + 'has no value on the record it was opened from.')
          return
        }
        // Match on DB columns. An unknown column is refused here rather than
        // sent: the engine drops a filter on a column it does not know, and a
        // narrower match silently widened would open the wrong record.
        const filters: ListFilter[] = []
        const unknown: string[] = []
        const described: string[] = []
        for (const [col, value] of Object.entries(payload.match)) {
          const c = index.get(norm(col))
          if (!c) { unknown.push(col); continue }
          filters.push({ column: c.dbName, op: 'eq', value })
          described.push(`${c.label} ${String(value)}`)
        }
        if (unknown.length || !filters.length) {
          cur.notify.error(`"${payload.actionName}" looks up ${cur.objTitle} by `
            + `${unknown.join(', ') || 'no column'}, which ${unknown.length === 1 ? 'is' : 'are'} not a column of it.`)
          return
        }
        const found = await cur.runtime.execute(cur.objName, 'list', {
          filters, ids_only: true,
        }) as { ids?: string[]; total?: number }
        const ids = (found?.ids || []).map(String)
        const total = typeof found?.total === 'number' ? found.total : ids.length
        if (total > 1) {
          cur.showMatches({
            filters,
            label: `${total} ${cur.objTitle} records match ${described.join(', ')} — open the one you want.`,
          })
          return
        }
        if (ids.length === 1 && payload.mode === 'view') {
          await cur.startEdit(ids[0], { forceView: true })
          return
        }
        if (ids.length === 1) {
          if (!cur.canEdit()) { cur.notify.error(OPEN_TARGET_DENIED); return }
          const opened = await cur.startEdit(ids[0])
          if (!opened) return
          if (opened.viewOnly) {
            ;(cur.notify.info ?? cur.notify.success)(
              `Opened the matching ${cur.objTitle} read-only — "${payload.actionName}" changed nothing on it.`)
            return
          }
          values = opened.header
        } else if (payload.mode === 'edit' || payload.mode === 'view') {
          cur.notify.error(`No ${cur.objTitle} matches ${described.join(', ')}.`)
          return
        } else {
          if (!cur.canAdd()) { cur.notify.error(OPEN_TARGET_DENIED); return }
          values = await cur.startAdd()
        }
      }
      if (!values) return
      let current: Record<string, unknown> = { ...values }

      // ── 2. Set the columns, each item_change landing before the next ──
      let lastHeaderChange: { key: string; value: unknown } | null = null
      for (const entry of payload.set) {
        const c = index.get(norm(entry.column))
        const key = c?.formKey || norm(entry.column)
        current = { ...current, [key]: entry.value }
        const settled = current
        ref.current.setFormValues(prev => ({ ...prev, [key]: settled[key] }))
        ref.current.setFormDirty(true)
        if (entry.protect) ref.current.setDynamicProtect(prev => ({ ...prev, [key]: 1 }))
        if (ref.current.headerChangeColumns.includes(key)) lastHeaderChange = { key, value: entry.value }
        if (entry.itemChange && c) {
          const outcome = await ref.current.itemChangeNow(key, current)
          current = { ...current, ...outcome.updates }
          if (!outcome.ok) {
            ref.current.setFormBanner(`Opened from "${payload.actionName}", but filling ${c.label} failed`
              + `${outcome.error ? `: ${outcome.error}` : ''}. Check the record before saving.`)
            return
          }
        }
      }

      // A line form that offers its lines when this column changes would do
      // it now — but only when the button did not say how lines arrive, or
      // they would be pulled twice.
      if (!payload.then.length && lastHeaderChange) {
        const hc = lastHeaderChange
        ref.current.setHeaderEdit(prev => ({ key: hc.key, value: hc.value, n: (prev?.n || 0) + 1 }))
      }

      // ── 3. The follow-up steps ────────────────────────────────────────
      for (const step of payload.then) {
        const label = step.run === 'line_command' ? String(step.command) : String(step.action_name)
        if (step.run === 'line_command') {
          const outcome = await runLineCommand(step)
          if (outcome === 'interactive') break  // the person finishes it; later steps would race them
          if (outcome !== 'added') {
            ref.current.setFormBanner(`Opened from "${payload.actionName}", but the step "${label}" `
              + `${outcome === 'failed' ? 'failed' : `could not run: ${outcome}`}.`)
            return
          }
        } else {
          const failed = await runActionStep(step, current)
          if (failed === 'cancelled') return
          if (failed) {
            ref.current.setFormBanner(`Opened from "${payload.actionName}", but the step "${label}" failed: ${failed}.`)
            return
          }
        }
      }
    } catch (err) {
      ref.current.setFormBanner(`Opened from "${payload.actionName}", but it stopped: ${errorText(err)}`)
      ref.current.notify.error(`"${payload.actionName}" stopped: ${errorText(err)}`)
    } finally {
      setRunning(null)
    }
  }, [runLineCommand, runActionStep])

  return { runOpen, running, lineRequest, onLineRequestDone }
}
